import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";

type Device = {
  deviceId: string;
  shortId?: string;
  status: string;
  name?: string | null;
  panelProfile?: string;
  layoutRevision?: number;
  lastSeen?: string | null;
  firmwareVersion?: string | null;
  claimedAt?: string;
};

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 0 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const Card = styled.div`
  border: 2px solid #111;
  background: #fff;
  box-shadow: 6px 6px 0 #111;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ShortId = styled.h3`
  margin: 0;
  font-size: 22px;
  letter-spacing: 0.04em;
`;

const ManageButton = styled(Link)`
  display: inline-block;
  border: 2px solid #111;
  padding: 6px 10px;
  background: #ffd24a;
  color: #111;
  text-decoration: none;
  font-weight: 700;
  box-shadow: 3px 3px 0 #111;
  align-self: flex-start;
  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0 #111;
  }
`;

const Title = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.35rem;
`;

const Err = styled.p`
  color: crimson;
  margin: 0;
`;

const AotdIrlMyFrames: React.FC = () => {
  const auth = useAppSelector((s) => s.auth);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ devices?: Device[] }>("/api/aotd-irl/devices");
        if (!cancelled) setDevices(res.data?.devices ?? []);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "failed to load frames";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }

  const claimed = devices.filter((d) => d.status === "registered");

  return (
    <Wrap>
      <Inner>
        <Title>My frames</Title>
        {loading && <p style={{ margin: "0.5rem 0 0" }}>Loading…</p>}
        {error && <Err>{error}</Err>}
        {!loading && claimed.length === 0 && (
          <p style={{ marginTop: "0.75rem", color: "#414141" }}>
            You don&apos;t have any claimed frames yet.{" "}
            <Link to="/aotd-irl/setup" style={{ fontWeight: 700, color: "#111" }}>
              Set up a frame
            </Link>{" "}
            ·{" "}
            <Link to="/aotd-irl/buy" style={{ fontWeight: 700, color: "#111" }}>
              Get one
            </Link>
          </p>
        )}
        <Grid>
          {claimed.map((d) => (
            <Card key={d.deviceId}>
              <ShortId>[{d.shortId ?? d.deviceId.slice(-4).toUpperCase()}]</ShortId>
              <div style={{ fontSize: 14, color: "#222", fontWeight: 600 }}>
                {d.name || (
                  <code style={{ wordBreak: "break-all", fontWeight: 400, color: "#666" }}>
                    {d.deviceId}
                  </code>
                )}
              </div>
              <ManageButton to={`/aotd-irl/my/${encodeURIComponent(d.deviceId)}`}>
                Manage
              </ManageButton>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Wrap>
  );
};

export default AotdIrlMyFrames;
